import React, { useState } from "react";

import { authenticationService } from "../../services/authentication";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { OperationRouter } from "../components/operation/router";
import { PalletRouter } from "../components/pallet/router";
import { routeReporting } from "./preparation-route-props";
import { VPPreparationMenu } from "../components/preparation-menu/vp-preparation-menu";
import { Header } from "../../common/component/header/header";
import { HeaderWidgetGroup } from "../../common/component/header/header-widget-group";
import { Breadcrumbs } from "../../common/component/breadcrumbs/breadcrumbs";
import { FrontendBreadcrumbsBuilder } from "../../common/component/breadcrumbs/frontend-breadcrumbs-builder";
import { LoaderIndicatorWidget } from "../../common/component/loader/loader-indicator-widget";
import { context } from "../../context/context";
import { Permission } from "../../context/permission";
import { PalletPickupFormWidget } from "../components/pallet-pickup/pallet-pickup-form-widget";
import { LanguageSelector } from "../../common/component/language-selector/language-selector";
import { UserAccountWidget } from "../components/user-account-widget/user-account-widget";
import { PreparationSectionType } from "./preparation-route-props";
import { BusinessDocumentationList } from "../components/business-documentation/business-documentation-list";
import { SupportTicketWidget } from "../components/support-ticket/support-ticket-widget";
import { WarehouseContextProvider } from "../components/pallet/warehouse-context/warehouse-context-provider";
import { RemotePrintingContextProvider } from "../../common/component/mobile-connector/remote-printing/remote-printing-context";
import { MobileConnectorWidget } from "../../common/component/mobile-connector/mobile-connector-widget";
import { BarcodeScannerTestPage } from "../components/barcode-scanner-test-page/barcode-scanner-test-page";
import { t } from "i18next";
import { NotificationWidget } from "../components/notification-widget/notification-widget";
import { SupplierContactDataValidationPopup } from "../components/supplier-contact-data-validation-popup/supplier-contact-data-validation-popup";

import "../../style/style.scss";
import "./style.scss";
import { BTMReturnsRouter } from "../components/btm-returns/router";

export const PreparationLayout = () => {
    const [defaultRouteExpanded, setDefaultRouteExpanded] = useState<PreparationSectionType>("operations");
    const [isContactDetailsValidationOpen, setIsContactDetailsValidationOpen] = useState<boolean>(false);
    const navigate = useNavigate();

    const settings = async () => {
        navigate("/settings");
    };

    const businessDocumentation = async () => {
        navigate("/business-documentation");
    };

    const barcodeTestPage = async () => {
        navigate("/settings/barcode-scanner-test");
    };

    const logout = async () => {
        if (context.isImpersonatedUser) {
            context.clearImpersonation();
            window.location.replace(`${window.location.origin}/back-office/user`);
        } else {
            await authenticationService.logout();
            navigate("/login");
            context.reset();
        }
    };

    const setRouteExpanded = (route: PreparationSectionType) => {
        setDefaultRouteExpanded(route);
    };

    const isEligibleForContactDetailsValidationOpen = context.isSupplier && context.hasPermission(Permission.FeatureUserDataValidation);

    return <div className="pageView">
        <div className="operationMenu">
            <VPPreparationMenu defaultRouteExpanded={defaultRouteExpanded} />
        </div>
        <WarehouseContextProvider user={context.currentUser}>
            <div className={`page-paper ${context.isImpersonatedUser ? "page-paper--impersonated" : ""}`}>
                <div className={"pageHeader"}>
                    <Header>
                        <HeaderWidgetGroup>
                            <Breadcrumbs builder={new FrontendBreadcrumbsBuilder()} />
                        </HeaderWidgetGroup>
                        <HeaderWidgetGroup>
                            <LoaderIndicatorWidget />
                            <NotificationWidget />
                            {context.isSupplier && <RemotePrintingContextProvider>
                                <MobileConnectorWidget />
                            </RemotePrintingContextProvider>}
                            <SupportTicketWidget />
                            {context.isSupplier && context.hasPermission(Permission.FeaturePalletPickUpForm) &&
                                <PalletPickupFormWidget />}
                            {isEligibleForContactDetailsValidationOpen && <SupplierContactDataValidationPopup />}
                            <LanguageSelector />
                            <UserAccountWidget name={context.currentUser.name}
                                               logoutText={t(context.isImpersonatedUser ? "navigation.backToBackoffice" : "navigation.logout")}
                                               onSettingsOpen={() => settings()}
                                               onBusinessDocumentationOpen={() => businessDocumentation()}
                                               onBarcodeTestPageOpen={() => barcodeTestPage()}
                                               onContactDetailsValidationOpen={isEligibleForContactDetailsValidationOpen ?
                                                   () => setIsContactDetailsValidationOpen(true) : undefined}
                                               onLogout={() => logout()} />
                        </HeaderWidgetGroup>
                    </Header>
                </div>
                <div className={"page-content__wrapper"}>
                    <Routes>
                        <Route path="operations/*" element={<OperationRouter setRouteExpanded={setRouteExpanded}/>} />
                        <Route path="pallet/*" element={<PalletRouter setRouteExpanded={setRouteExpanded} />} />
                        <Route path="btm-returns/*" element={<BTMReturnsRouter setRouteExpanded={setRouteExpanded} />} />
                        <Route path="/business-documentation" element={<BusinessDocumentationList />} />
                        <Route path="/barcode-scanner-test" element={<BarcodeScannerTestPage />} />
                        <Route path="/" element={<Navigate to={`/operations/${routeReporting}`} />} />
                    </Routes>
                </div>
            </div>
            {isContactDetailsValidationOpen &&
                <SupplierContactDataValidationPopup isVoluntary={true}
                                                    onClose={() => setIsContactDetailsValidationOpen(false)} />}
        </WarehouseContextProvider>
    </div>;
};
